import { getOfficesByFirmId } from "../Services/Firm";
import {
  getFirmContactByFirmId,
  getFirmContactByOfficeId,
} from "../Services/FirmContact";
import type { FirmContactView } from "../interfaces/models/IFirmContact";
import { buildAccountingFirmOfficeGroups } from "./accountingOfficeTableGrouping";
import type { AccountingOfficeDetail } from "./accountingOfficeDetail";
import { resolveFirmJurisdictionDisplay } from "./resolveFirmJurisdictionDisplay";
import {
  resolveContactValidationFromView,
  type FirmContactValidation,
} from "./firmContactUtils";
import type { BillingSendRecipients } from "./billingSendRecipients";

export type AccountingContactResolution = {
  validation: FirmContactValidation;
  billingRecipients: BillingSendRecipients;
  accountingContactName: string;
  ccContactDisplay: string;
  usesPrimaryOfficeContacts: boolean;
  inheritNotice: string | null;
  primaryJurisdictionName: string;
};

export function buildInheritPrimaryContactsNotice(
  primaryJurisdictionName: string,
  missing: { primary: boolean; accounting: boolean },
): string {
  const jurisdiction =
    String(primaryJurisdictionName ?? "").trim() || "Primary";

  if (missing.primary && missing.accounting) {
    return `Primary and Accounting Contacts are not assigned, ${jurisdiction}'s contacts will be used`;
  }
  if (missing.primary) {
    return `Primary Contact is not assigned, ${jurisdiction}'s contacts will be used`;
  }
  if (missing.accounting) {
    return `Accounting Contact is not assigned, ${jurisdiction}'s contacts will be used`;
  }
  return "";
}

async function getDirectFirmContactForOffice(
  officeId: string,
  firmId?: string,
): Promise<FirmContactView | null> {
  const trimmedOfficeId = officeId.trim();
  if (!trimmedOfficeId) {
    return null;
  }

  const direct = await getFirmContactByOfficeId(trimmedOfficeId).catch(
    () => undefined,
  );
  if (direct) {
    return direct;
  }

  const trimmedFirmId = String(firmId ?? "").trim();
  if (!trimmedFirmId) {
    return null;
  }

  const firmContacts = await getFirmContactByFirmId(trimmedFirmId).catch(
    () => [],
  );
  return (
    firmContacts.find(
      (contact) => String(contact.officeId ?? "").trim() === trimmedOfficeId,
    ) ?? null
  );
}

function resolveCcContactFromFirmContact(
  cc?: FirmContactView["ccContact"],
): { email: string; name: string } {
  const email = String(cc?.email ?? "").trim();
  const name = String(cc?.fullName ?? "").trim();
  return { email, name };
}

function isNonPrimaryOfficeInGroup(
  office: Pick<AccountingOfficeDetail, "id" | "membershipType">,
  primaryOfficeId: string | null,
): boolean {
  const officeId = String(office.id ?? "").trim();
  const primaryId = String(primaryOfficeId ?? "").trim();
  if (!primaryId || primaryId === officeId) {
    return false;
  }

  const membership = String(office.membershipType ?? "").toLowerCase();
  if (membership && membership !== "primary") {
    return true;
  }

  return primaryId !== officeId;
}

function officeNeedsContactInheritance(validation: FirmContactValidation): boolean {
  return !validation.hasPrimaryContact || !validation.hasAccountingContact;
}

function contactSlotCanBeInherited(
  slot: { id?: string; email?: string; fullName?: string } | undefined,
): boolean {
  const id = String(slot?.id ?? "").trim();
  const email = String(slot?.email ?? "").trim();
  return Boolean(id && email);
}

function resolveInheritancePlan(params: {
  officeValidation: FirmContactValidation;
  primaryContact: FirmContactView | null;
  nonPrimary: boolean;
}): {
  inheritPrimaryContact: boolean;
  inheritAccountingContact: boolean;
} {
  if (!params.nonPrimary || !params.primaryContact) {
    return { inheritPrimaryContact: false, inheritAccountingContact: false };
  }

  const primaryValidation = resolveContactValidationFromView(params.primaryContact);

  const inheritPrimaryContact =
    !params.officeValidation.hasPrimaryContact &&
    primaryValidation.hasPrimaryContact &&
    contactSlotCanBeInherited(params.primaryContact.primaryContact);

  const inheritAccountingContact =
    !params.officeValidation.hasAccountingContact &&
    primaryValidation.hasAccountingContact &&
    contactSlotCanBeInherited(params.primaryContact.accountingContact);

  return { inheritPrimaryContact, inheritAccountingContact };
}

function mergeContactValidation(
  officeContact: FirmContactView | null,
  primaryContact: FirmContactView | null,
  inherit: { primary: boolean; accounting: boolean },
): FirmContactValidation {
  const officeVal = resolveContactValidationFromView(officeContact);
  if (!primaryContact) {
    return officeVal;
  }

  const primaryVal = resolveContactValidationFromView(primaryContact);

  const hasPrimary =
    officeVal.hasPrimaryContact ||
    (inherit.primary && primaryVal.hasPrimaryContact);
  const hasAccounting =
    officeVal.hasAccountingContact ||
    (inherit.accounting && primaryVal.hasAccountingContact);

  const primaryContactId = officeVal.hasPrimaryContact
    ? officeVal.primaryContactId
    : inherit.primary
      ? primaryVal.primaryContactId
      : "";
  const primaryContactName = officeVal.hasPrimaryContact
    ? officeVal.primaryContactName
    : inherit.primary
      ? primaryVal.primaryContactName
      : "";
  const accountingContactId = officeVal.hasAccountingContact
    ? officeVal.accountingContactId
    : inherit.accounting
      ? primaryVal.accountingContactId
      : "";
  const accountingContactName = officeVal.hasAccountingContact
    ? officeVal.accountingContactName
    : inherit.accounting
      ? primaryVal.accountingContactName
      : "";

  const canCompare = Boolean(primaryContactId && accountingContactId);

  return {
    hasPrimaryContact: hasPrimary,
    primaryContactId,
    primaryContactName,
    hasAccountingContact: hasAccounting,
    accountingContactId,
    accountingContactName,
    canComparePrimaryAndAccounting: canCompare,
    primaryAndAccountingSamePerson: canCompare
      ? primaryContactId === accountingContactId
      : false,
  };
}

function pickEffectiveContactField(
  officeSlot: { id?: string; fullName?: string; email?: string } | undefined,
  primarySlot: { id?: string; fullName?: string; email?: string } | undefined,
  officeHasAssignment: boolean,
  inheritFromPrimary: boolean,
): { email: string; name: string; inherited: boolean } {
  const ownEmail = String(officeSlot?.email ?? "").trim();
  const ownName = String(officeSlot?.fullName ?? "").trim();
  if (ownEmail) {
    return { email: ownEmail, name: ownName, inherited: false };
  }

  if (inheritFromPrimary && !officeHasAssignment) {
    const inheritedEmail = String(primarySlot?.email ?? "").trim();
    const inheritedName = String(primarySlot?.fullName ?? "").trim();
    if (inheritedEmail) {
      return { email: inheritedEmail, name: inheritedName, inherited: true };
    }
  }

  return { email: "", name: ownName, inherited: false };
}

function buildMergedBillingRecipients(params: {
  officeContact: FirmContactView | null;
  primaryContact: FirmContactView | null;
  officeValidation: FirmContactValidation;
  inheritPrimaryContact: boolean;
  inheritAccountingContact: boolean;
  ccEmail: string;
  ccName: string;
  inheritNotice: string | null;
  primaryJurisdictionName: string;
}): BillingSendRecipients {
  const usesPrimaryOfficeContacts =
    params.inheritPrimaryContact || params.inheritAccountingContact;

  const merged = mergeContactValidation(
    params.officeContact,
    usesPrimaryOfficeContacts ? params.primaryContact : null,
    {
      primary: params.inheritPrimaryContact,
      accounting: params.inheritAccountingContact,
    },
  );

  const primaryField = pickEffectiveContactField(
    params.officeContact?.primaryContact,
    params.primaryContact?.primaryContact,
    params.officeValidation.hasPrimaryContact,
    params.inheritPrimaryContact,
  );
  const accountingField = pickEffectiveContactField(
    params.officeContact?.accountingContact,
    params.primaryContact?.accountingContact,
    params.officeValidation.hasAccountingContact,
    params.inheritAccountingContact,
  );

  return {
    primaryEmail: primaryField.email,
    primaryName: primaryField.name || merged.primaryContactName,
    accountingEmail: accountingField.email,
    accountingName: accountingField.name || merged.accountingContactName,
    secondaryEmail: String(
      params.officeContact?.secondaryContact?.email ?? "",
    ).trim(),
    secondaryName: String(
      params.officeContact?.secondaryContact?.fullName ?? "",
    ).trim(),
    ccEmail: params.ccEmail,
    ccName: params.ccName,
    hasPrimaryContact: merged.hasPrimaryContact,
    hasAccountingContact: merged.hasAccountingContact,
    primaryAndAccountingSamePerson: merged.primaryAndAccountingSamePerson,
    canComparePrimaryAndAccounting: merged.canComparePrimaryAndAccounting,
    usesPrimaryOfficeContacts,
    inheritNotice: params.inheritNotice,
    primaryEmailInherited: primaryField.inherited,
    accountingEmailInherited: accountingField.inherited,
    primaryJurisdictionName: params.primaryJurisdictionName,
  };
}

async function resolvePrimaryOfficeContext(
  office: AccountingOfficeDetail,
): Promise<{ primaryOfficeId: string | null; primaryJurisdictionName: string }> {
  const firmId = String(office.firm ?? "").trim();
  if (!firmId) {
    return { primaryOfficeId: null, primaryJurisdictionName: "" };
  }

  const siblings = await getOfficesByFirmId(firmId);
  const group = buildAccountingFirmOfficeGroups(siblings).get(firmId);
  const primaryOfficeId = group?.parentOfficeId ?? null;

  if (!primaryOfficeId) {
    return { primaryOfficeId: null, primaryJurisdictionName: "" };
  }

  const primaryOffice = siblings.find(
    (item) => String(item.id ?? "").trim() === primaryOfficeId,
  );
  const primaryJurisdictionName = String(
    primaryOffice?.jurisdictionName ??
      resolveFirmJurisdictionDisplay(
        primaryOffice?.jurisdiction,
        primaryOffice,
      ) ??
      "",
  ).trim();

  return { primaryOfficeId, primaryJurisdictionName };
}

/**
 * Resolves firm contacts for accounting UI and billing email delivery.
 * Non-primary offices without primary/accounting contacts inherit the primary
 * office's contacts in memory only (no database writes).
 */
export async function resolveAccountingOfficeContacts(
  office: AccountingOfficeDetail,
): Promise<AccountingContactResolution> {
  const officeId = String(office.id ?? "").trim();
  const firmId = String(office.firm ?? "").trim();

  const [officeContact, primaryContext] = await Promise.all([
    getDirectFirmContactForOffice(officeId, firmId),
    resolvePrimaryOfficeContext(office),
  ]);

  const officeValidation = resolveContactValidationFromView(officeContact);
  const nonPrimary = isNonPrimaryOfficeInGroup(
    office,
    primaryContext.primaryOfficeId,
  );
  const needsInheritance =
    nonPrimary && officeNeedsContactInheritance(officeValidation);

  let primaryContact: FirmContactView | null = null;
  if (needsInheritance && primaryContext.primaryOfficeId) {
    primaryContact = await getDirectFirmContactForOffice(
      primaryContext.primaryOfficeId,
      firmId,
    );
  }

  const { inheritPrimaryContact, inheritAccountingContact } =
    resolveInheritancePlan({
      officeValidation,
      primaryContact,
      nonPrimary,
    });

  const usesPrimaryOfficeContacts =
    inheritPrimaryContact || inheritAccountingContact;

  const inheritNotice = usesPrimaryOfficeContacts
    ? buildInheritPrimaryContactsNotice(
        primaryContext.primaryJurisdictionName,
        {
          primary: inheritPrimaryContact,
          accounting: inheritAccountingContact,
        },
      ) || null
    : null;

  const ccResolved = resolveCcContactFromFirmContact(officeContact?.ccContact);

  const validation = usesPrimaryOfficeContacts
    ? mergeContactValidation(officeContact, primaryContact, {
        primary: inheritPrimaryContact,
        accounting: inheritAccountingContact,
      })
    : officeValidation;

  const billingRecipients = buildMergedBillingRecipients({
    officeContact,
    primaryContact,
    officeValidation,
    inheritPrimaryContact,
    inheritAccountingContact,
    ccEmail: ccResolved.email,
    ccName: ccResolved.name,
    inheritNotice,
    primaryJurisdictionName: primaryContext.primaryJurisdictionName,
  });

  const accountingContactName =
    billingRecipients.accountingName ||
    validation.accountingContactName ||
    billingRecipients.primaryName ||
    validation.primaryContactName ||
    "";

  return {
    validation,
    billingRecipients,
    accountingContactName,
    ccContactDisplay:
      ccResolved.name ||
      ccResolved.email ||
      String(officeContact?.ccContact?.fullName ?? "").trim() ||
      String(officeContact?.ccContact?.email ?? "").trim(),
    usesPrimaryOfficeContacts,
    inheritNotice,
    primaryJurisdictionName: primaryContext.primaryJurisdictionName,
  };
}
